import { FaBookOpen, FaCrown, FaSignOutAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";

export default function HeaderActions({ tenantId, fullName, onUpgrade, docRef, upgradeRef, isAdmin = true }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayName = fullName || "User";
  
  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.trim().split(/\s+/);
    if (names.length === 0) return "U";
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(displayName);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div className="flex items-center gap-3">
      {/* Documentation Button */}
      <div className="relative group">
        <motion.button
          ref={docRef}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() =>
            window.open(
              "https://documentation.droog.io/quickstart#registration",
              "_blank"
            )
          }
          aria-label="View documentation"
          className="
            w-9 h-9
            rounded-full
            bg-white
            border border-[#E5DDF0]
            flex items-center justify-center
            text-[#6B7280]
            shadow-sm
            hover:bg-[#F7F2FA]
            hover:text-[#5B3A60]
            hover:border-[#5B3A60]
            hover:shadow-md
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-[#5B3A60] focus:ring-offset-2
          "
        >
          <FaBookOpen className="w-4 h-4" />
        </motion.button>

        <div className="absolute right-0 top-full mt-2 px-2 py-1 text-[11px] font-medium rounded bg-[#5B3A60] text-white opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap pointer-events-none shadow-lg z-40">
          Documentation
          <div className="absolute -top-1 right-3 w-2 h-2 bg-[#5B3A60] rotate-45" />
        </div>
      </div>

      {/* 🔑 Upgrade Button - Only show if isAdmin is true */}
      {isAdmin && (
        <motion.button
          ref={upgradeRef}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() =>
            onUpgrade
              ? onUpgrade()
              : navigate(`/billing/${tenantId}`)
          }
          className="
            px-4 py-2
            rounded-full
            bg-gradient-to-r from-[#5B3A60] to-[#7B5E57]
            text-white
            font-medium text-sm
            shadow-sm
            hover:shadow-md
            transition-all duration-200
            flex items-center gap-1.5
            focus:outline-none focus:ring-2 focus:ring-[#5B3A60] focus:ring-offset-2
          "
        >
          <FaCrown className="w-3.5 h-3.5" />
          <span>Upgrade</span>
        </motion.button>
      )}

      {/* Profile Avatar & Logout Menu */}
      <div className="relative" ref={profileRef}>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowMenu(!showMenu)}
          className="
            w-9 h-9
            rounded-full
            bg-gradient-to-br from-[#5B3A60] to-[#7B5E57]
            text-white
            flex items-center justify-center
            text-xs font-bold tracking-wide
            shadow-sm
            hover:shadow-md
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-[#5B3A60] focus:ring-offset-2
            cursor-pointer
          "
        >
          {initials}
        </motion.button>

        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg shadow-xl border border-[#E4DCE6] z-50 overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-[#E4DCE6] bg-[#FAF8FB]">
                <p className="text-xs text-[#7A6B7E] font-medium uppercase tracking-wider">Signed in as</p>
                <p className="text-sm font-bold text-[#3D2542] truncate">{displayName}</p>
              </div>

              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 text-sm text-[#C62828] hover:bg-[#FDEEEE] flex items-center gap-2 transition-colors"
              >
                <FaSignOutAlt className="w-4 h-4" />
                <span className="font-medium">Log out</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}